import { MEDIA_VARIANT_WIDTHS, MediaVariant } from '@tbbt/shared';
import { env } from '@/config/env';

/**
 * Build the URL for a media rendition.
 *
 * Centralised so no component ever assembles a media path by hand. That matters
 * because the URL shape is not a frontend concern: under the `s3` storage driver the
 * API returns CDN URLs instead, and only the server knows which. Keeping construction
 * in one place means switching drivers does not touch a single component.
 */
export function mediaUrl(
  mediaId: string,
  variant: keyof typeof MEDIA_VARIANT_WIDTHS | 'original' = 'large',
): string {
  return `${env.apiBaseUrl}/public/media/${mediaId}/${variant}`;
}

/**
 * Build a `srcset` so the browser downloads a rendition matched to the display size,
 * rather than a 1600px hero for a 320px card.
 */
export function mediaSrcSet(mediaId: string): string {
  return (Object.entries(MEDIA_VARIANT_WIDTHS) as [keyof typeof MEDIA_VARIANT_WIDTHS, number][])
    .sort((a, b) => a[1] - b[1])
    .map(([variant, width]) => `${mediaUrl(mediaId, variant)} ${width}w`)
    .join(', ');
}

/**
 * Default `sizes` for a card image in a responsive grid.
 *
 * Without a `sizes` attribute the browser assumes the image fills the viewport width
 * and picks the largest rendition — which defeats the entire point of generating
 * smaller ones.
 */
export const CARD_IMAGE_SIZES = '(min-width: 1024px) 380px, (min-width: 640px) 50vw, 100vw';
export const HERO_IMAGE_SIZES = '(min-width: 1024px) 1240px, 100vw';
/**
 * For an image filling one half of a two-column split — the homepage hero and the
 * "Why we exist" block. Half of the 1240px container, less the gap.
 */
export const SPLIT_IMAGE_SIZES = '(min-width: 1024px) 590px, 100vw';

export { MediaVariant };
